import { getInitials } from "./format";
import type { BusinessType } from "./zod/gym";

export const GENDER_OPTIONS = [
  { value: "", label: "Seleccionar..." },
  { value: "MALE", label: "Masculino" },
  { value: "FEMALE", label: "Femenino" },
  { value: "OTHER", label: "Otro" },
  { value: "PREFER_NOT_TO_SAY", label: "Prefiero no decir" },
];

const GENDER_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  GENDER_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);

export function getGenderLabel(value: string | null | undefined): string {
  if (!value) return "-";
  return GENDER_LABEL_BY_VALUE[value] ?? value;
}

export const BUSINESS_SIZE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Seleccionar..." },
  { value: "small", label: "Pequeña" },
  { value: "medium", label: "Mediana" },
  { value: "large", label: "Grande" },
];

// El backend solo acepta el enum small/medium/large; datos legacy pueden tener
// la etiqueta en español o distinta capitalización ("GRANDE", "Pequeña").
const BUSINESS_SIZE_BY_LEGACY: Record<string, string> = {
  small: "small",
  pequeña: "small",
  pequena: "small",
  pequeño: "small",
  pequeno: "small",
  medium: "medium",
  mediana: "medium",
  mediano: "medium",
  large: "large",
  grande: "large",
};

export function normalizeBusinessSize(
  value: string | null | undefined,
): string {
  if (!value) return "";
  return BUSINESS_SIZE_BY_LEGACY[value.trim().toLowerCase()] ?? value;
}

// El backend responde siempre en inglés; el front muestra mensajes en español.
const API_ERROR_TRANSLATIONS: Array<{ pattern: RegExp; message: string }> = [
  {
    pattern: /^businessSize must be one of/i,
    message: "Tamaño de Empresa debe ser: Pequeña, Mediana o Grande",
  },
  {
    pattern: /must be a valid email address/i,
    message: "El correo electrónico no es válido",
  },
];

export function translateClientApiError(
  message: string | undefined,
): string | null {
  if (!message) return null;
  return (
    API_ERROR_TRANSLATIONS.find((t) => t.pattern.test(message))?.message ?? null
  );
}

/**
 * Garantiza que el valor guardado aparezca en el select aunque no exista en el
 * catálogo de opciones (datos legacy/importados); evita que se muestre vacío y
 * se borre al guardar.
 */
export function withCurrentOption(
  options: Array<{ value: string; label: string }>,
  current: string | null | undefined,
  label?: string,
): Array<{ value: string; label: string }> {
  if (!current || options.some((o) => o.value === current)) return options;
  return [...options, { value: current, label: label ?? current }];
}

type ClientNameParts = {
  firstName?: string | null;
  lastName?: string | null;
  businessName?: string | null;
};

/**
 * Nombre a mostrar para un cliente persona o empresa.
 * Persona: "Nombre Apellido"; empresa: el nombre corto (`firstName`) o, si no
 * hay, la razón social. En negocios B2B la prioridad se invierte: razón social
 * primero, con fallback al nombre de contacto. Devuelve "—" si no hay nada.
 */
export function getClientDisplayName(
  client: ClientNameParts,
  businessType?: BusinessType,
): string {
  const fullName = [client.firstName, client.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const businessName = client.businessName?.trim() ?? "";
  if (businessType === "B2B") return businessName || fullName || "—";
  return fullName || businessName || "—";
}

/** Iniciales para el avatar; usa la razón social si no hay nombre ni apellido. */
export function getClientInitials(
  client: ClientNameParts,
  businessType?: BusinessType,
): string {
  const firstName = client.firstName?.trim() ?? "";
  const lastName = client.lastName?.trim() ?? "";
  const businessName = client.businessName?.trim() ?? "";
  if ((businessType === "B2B" && businessName) || (!firstName && !lastName)) {
    return businessName ? businessName[0]!.toUpperCase() : "?";
  }
  return getInitials(firstName, lastName);
}
